// EntityViewer.js
//
// Build 27 (item 7): read-only inline viewer for defects and feature
// requests, opened from desktop shortcuts WITHOUT navigating away from
// the desktop. Mirrors the Build 26 NoteViewer pattern (same overlay
// styling, Escape-to-close, Copy Link / Pin / fullscreen toolbar) but
// is read-only — editing still happens in the full app via the
// "Open full editor" button.
//
// Why read-only: defects and features have heavy editors (status
// workflows, comments, attachments, voting). Re-implementing those in
// an overlay would mean porting two whole apps. A read-only snapshot
// gives the desktop-consistency win without that scope.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { buildPermalink, copyToClipboard, pinEntityToDesktop } from './utils/shortcutHelpers';
import './NoteViewer.css'; // reuse the exact overlay/modal/toolbar styling

function Field({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' }}>
        {label}
      </div>
      <div style={{ fontSize: '0.95rem', color: '#111827', whiteSpace: 'pre-wrap' }}>
        {value}
      </div>
    </div>
  );
}

function EntityViewer({
  entity,                 // the defect or feature object (with id)
  entityType,             // 'defect' | 'feature'
  onClose,
  onOpenFullEditor,       // () => void  — navigates to the full app
  currentUserId,
  showPinToDesktop = false,
  embedded = false,         // Build 28: hosted inside a FloatingWindow
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, onClose]);

  if (!entity) return null;

  const flash = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 2200);
  };

  const routePrefix = entityType === 'feature' ? '/feature-requests' : '/defect-tracker';
  const icon = entityType === 'feature' ? '✨' : '🐛';
  const typeLabel = entityType === 'feature' ? 'Feature Request' : 'Defect';

  const handleCopyLink = async () => {
    const url = buildPermalink(routePrefix, entity.id);
    const ok = await copyToClipboard(url);
    flash(ok ? 'success' : 'error', ok ? 'Link copied to clipboard' : 'Could not copy link');
  };

  const handlePin = async () => {
    if (!currentUserId) {
      flash('error', 'Sign in to pin to desktop');
      return;
    }
    try {
      await pinEntityToDesktop({
        userId: currentUserId,
        type: entityType,
        entityId: entity.id,
        title: entity.title,
        icon
      });
      flash('success', 'Pinned to desktop');
    } catch (err) {
      console.error('Pin failed:', err);
      flash('error', `Could not pin: ${err?.message || 'unknown error'}`);
    }
  };

  // Build 28 v1.0.1: same fix as NoteViewer — when embedded AND
  // fullscreen, break out of the window to a full-screen overlay
  // (instead of the embedded branch swallowing isFullscreen).
  const useEmbeddedChrome = embedded && !isFullscreen;
  const overlayClass = useEmbeddedChrome
    ? 'note-viewer-overlay note-viewer-overlay--embedded'
    : `note-viewer-overlay ${isFullscreen ? 'note-viewer-overlay--fullscreen' : ''}`;
  const modalClass = useEmbeddedChrome
    ? 'note-viewer-modal note-viewer-modal--embedded'
    : `note-viewer-modal ${isFullscreen ? 'note-viewer-modal--fullscreen' : ''}`;

  const tree = (
    <div
      className={overlayClass}
      onClick={(e) => {
        if (useEmbeddedChrome) return;
        if (embedded) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="note-viewer-header">
          <div className="note-viewer-title-row">
            <span className="note-viewer-icon">{icon}</span>
            <h2 className="note-viewer-title">{entity.title || `Untitled ${typeLabel}`}</h2>
          </div>
          <div className="note-viewer-toolbar no-print">
            <span className="view-only-badge" style={{ background: '#fef3c7', color: '#92400e', padding: '0.2rem 0.55rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 600, alignSelf: 'center' }}>
              👁️ View Only
            </span>
            <button className="note-viewer-tool-btn" onClick={handleCopyLink} title="Copy a shareable link">
              🔗 Copy Link
            </button>
            {showPinToDesktop && (
              <button className="note-viewer-tool-btn" onClick={handlePin} title="Pin to your desktop">
                📎 Pin to Desktop
              </button>
            )}
            {onOpenFullEditor && (
              <button className="note-viewer-tool-btn" onClick={onOpenFullEditor} title={`Open this ${typeLabel.toLowerCase()} in the full app`}>
                ✏️ Open full editor
              </button>
            )}
            <button
              className="note-viewer-tool-btn"
              onClick={() => setIsFullscreen(v => !v)}
              title={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
            >
              {isFullscreen ? '🗗 Exit fullscreen' : '🗖 Fullscreen'}
            </button>
            <button className="note-viewer-close-btn" onClick={onClose} title="Close (Esc)" aria-label="Close">
              ×
            </button>
          </div>
        </div>

        <div className="note-viewer-meta">
          {entity.status && <span className="note-viewer-meta-item">Status: <strong>{entity.status}</strong></span>}
          {entity.priority && <span className="note-viewer-meta-item">Priority: <strong>{entity.priority}</strong></span>}
          {entity.createdAt?.toDate && (
            <span className="note-viewer-meta-item">Created {entity.createdAt.toDate().toLocaleDateString()}</span>
          )}
        </div>

        <div className="note-viewer-body">
          {entityType === 'defect' ? (
            <>
              <Field label="Application" value={entity.applicationName} />
              <Field label="Title" value={entity.title} />
              <Field label="Test Step" value={entity.testStep} />
              <Field label="Expected Result" value={entity.expectedResult} />
              {entity.expectedResultImageUrl && (
                <div style={{ marginBottom: '0.85rem' }}>
                  <img src={entity.expectedResultImageUrl} alt="Expected" style={{ maxWidth: '100%', borderRadius: '4px' }} />
                </div>
              )}
              <Field label="Actual Result" value={entity.actualResult} />
              {entity.actualResultImageUrl && (
                <div style={{ marginBottom: '0.85rem' }}>
                  <img src={entity.actualResultImageUrl} alt="Actual" style={{ maxWidth: '100%', borderRadius: '4px' }} />
                </div>
              )}
              <Field label="Priority" value={entity.priority} />
              <Field label="Status" value={entity.status} />
              <Field label="Assigned to Fix" value={entity.assignedToFix || 'Unassigned'} />
              <Field label="Assigned to Test" value={entity.assignedToTest || 'Unassigned'} />
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
                  Comments ({entity.comments?.length || 0})
                </div>
                {(entity.comments || []).map((c, idx) => (
                  <div key={idx} style={{ background: '#f9fafb', borderRadius: '0.375rem', padding: '0.5rem 0.65rem', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                    <div style={{ color: '#374151' }}>{c.text}</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.72rem', marginTop: '0.2rem' }}>
                      {c.author || 'Unknown'}{c.createdAt?.toDate ? ` · ${c.createdAt.toDate().toLocaleString()}` : ''}
                    </div>
                  </div>
                ))}
                {(!entity.comments || entity.comments.length === 0) && (
                  <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85rem' }}>No comments</div>
                )}
              </div>
            </>
          ) : (
            <>
              <Field label="Title" value={entity.title} />
              <Field label="Description" value={entity.description} />
              <Field label="Status" value={entity.status} />
              <Field label="Priority" value={entity.priority} />
              <Field label="Votes" value={typeof entity.voteCount === 'number' ? String(entity.voteCount) : (entity.votes ? String(entity.votes.length) : undefined)} />
              <Field label="Category" value={entity.category} />
              <Field label="Requested By" value={entity.createdByEmail || entity.requestedBy} />
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
                  Comments ({entity.comments?.length || 0})
                </div>
                {(entity.comments || []).map((c, idx) => (
                  <div key={idx} style={{ background: '#f9fafb', borderRadius: '0.375rem', padding: '0.5rem 0.65rem', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                    <div style={{ color: '#374151' }}>{c.text}</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.72rem', marginTop: '0.2rem' }}>
                      {c.author || 'Unknown'}{c.createdAt?.toDate ? ` · ${c.createdAt.toDate().toLocaleString()}` : ''}
                    </div>
                  </div>
                ))}
                {(!entity.comments || entity.comments.length === 0) && (
                  <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85rem' }}>No comments</div>
                )}
              </div>
            </>
          )}
        </div>

        {feedback && (
          <div className={`note-viewer-toast note-viewer-toast--${feedback.type}`}>
            {feedback.text}
          </div>
        )}
      </div>
    </div>
  );

  // Build 28 v1.0.2: portal to body when a windowed defect/feature
  // breaks out to fullscreen, escaping the FloatingWindow clip.
  if (embedded && isFullscreen) {
    return createPortal(tree, document.body);
  }
  return tree;
}

export default EntityViewer;
